import * as React from 'react';
import { cn } from '../../../utils';

const TICK_COUNT = 40;

/** Fixed pseudo-random tick-bar heights (px), deterministic like the spec. */
const TICK_HEIGHTS: number[] = Array.from({ length: TICK_COUNT }, (_, i) => {
  const wave = 16 + 9 * Math.sin(i * 1.7) + 6 * Math.sin(i * 0.53 + 2);
  return Math.max(6, Math.min(30, Math.round(wave)));
});

function formatTime(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export interface AudioStripProps {
  /** audio source URL */
  src: string;
  /** caption line rendered under the strip */
  caption?: React.ReactNode;
  /** called once, the first time playback starts */
  onFirstPlay?: () => void;
  /** aria-label for the play button while paused */
  playLabel?: string;
  /** aria-label for the play button while playing */
  pauseLabel?: string;
  /** aria-label for the seek slider */
  seekLabel?: string;
  className?: string;
}

/**
 * Magazine audio strip: round red play/pause button, tick-bar progress
 * (bars turn sizzle-pink as playback passes them; click/drag or use
 * arrow keys to seek) and tabular "0:38 / 1:24" time. Wraps a hidden
 * <audio> element — no external deps.
 */
export function AudioStrip({
  src,
  caption,
  onFirstPlay,
  playLabel = 'Play audio',
  pauseLabel = 'Pause audio',
  seekLabel = 'Seek',
  className,
}: AudioStripProps) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const ticksRef = React.useRef<HTMLDivElement>(null);
  const draggingRef = React.useRef(false);
  const firstPlayFiredRef = React.useRef(false);

  const [playing, setPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  };

  const handlePlay = () => {
    setPlaying(true);
    if (!firstPlayFiredRef.current) {
      firstPlayFiredRef.current = true;
      onFirstPlay?.();
    }
  };

  const handleDurationChange = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
  };

  const seekToTime = (time: number) => {
    const audio = audioRef.current;
    if (!audio || duration <= 0) return;
    const clamped = Math.min(Math.max(time, 0), duration);
    audio.currentTime = clamped;
    setCurrentTime(clamped);
  };

  const seekToClientX = (clientX: number) => {
    const ticks = ticksRef.current;
    if (!ticks || duration <= 0) return;
    const rect = ticks.getBoundingClientRect();
    if (rect.width <= 0) return;
    const fraction = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    seekToTime(fraction * duration);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekToClientX(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (draggingRef.current) seekToClientX(e.clientX);
  };

  const stopDragging = () => {
    draggingRef.current = false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      seekToTime(currentTime - 5);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      seekToTime(currentTime + 5);
    } else if (e.key === 'Home') {
      e.preventDefault();
      seekToTime(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      seekToTime(duration);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      togglePlay();
    }
  };

  const fraction = duration > 0 ? currentTime / duration : 0;

  return (
    <>
      <div className={cn('z-audio-strip', className)}>
        <button
          type="button"
          className="z-play"
          aria-label={playing ? pauseLabel : playLabel}
          onClick={togglePlay}
        >
          {playing ? (
            <svg width="13" height="15" viewBox="0 0 14 16" fill="currentColor" aria-hidden="true">
              <path d="M1 0h4v16H1zM9 0h4v16H9z" />
            </svg>
          ) : (
            <svg
              className="z-tri"
              width="13"
              height="15"
              viewBox="0 0 14 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M0 0l14 8-14 8z" />
            </svg>
          )}
        </button>
        <div
          className="z-ticks"
          ref={ticksRef}
          role="slider"
          tabIndex={0}
          aria-label={seekLabel}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(currentTime)}
          aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDragging}
          onPointerCancel={stopDragging}
          onKeyDown={handleKeyDown}
        >
          {TICK_HEIGHTS.map((height, i) => (
            <i
              key={i}
              className={i / TICK_COUNT < fraction ? 'z-past' : undefined}
              style={{ height }}
            />
          ))}
        </div>
        <span className="z-time">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onPlay={handlePlay}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
          onLoadedMetadata={handleDurationChange}
          onDurationChange={handleDurationChange}
        />
      </div>
      {caption != null && <p className="z-caption">{caption}</p>}
    </>
  );
}
